/**
 * Utilities de datas PT-PT.
 * Sem date-fns nesta camada — apenas Intl + Date nativo (mais leve, suficiente para
 * formatação e parsing de Mês_Chave). date-fns entra se for preciso math complexa
 * em F2+ (diffInDays, addMonths para vencimentos de faturas).
 */

export const MONTH_NAMES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

const MONTH_INDEX_PT = new Map<string, number>(
  MONTH_NAMES_PT.flatMap((name, i) => [
    [name.toLowerCase(), i],
    // Variantes sem acento para tolerância (paths usam "marco" em vez de "março").
    [name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''), i],
  ]),
);

const DATE_SHORT = new Intl.DateTimeFormat('pt-PT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATE_LONG = new Intl.DateTimeFormat('pt-PT', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const DATETIME_SHORT = new Intl.DateTimeFormat('pt-PT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `25/05/2026`. */
export function formatDateShort(d: Date): string {
  return DATE_SHORT.format(d);
}

/** `25 de maio de 2026`. */
export function formatDateLong(d: Date): string {
  return DATE_LONG.format(d);
}

/** `25/05/2026 14:32`. */
export function formatDateTime(d: Date): string {
  return DATETIME_SHORT.format(d);
}

/**
 * Parser para Mês_Chave (col G da sheet). Aceita:
 *  - "Março 2026" (padrão da sheet)
 *  - "março 2026" (lowercase)
 *  - "marco 2026" (sem acentos — paths)
 *  - "MARÇO 2026"
 * Devolve { year, month0Based } ou null se inválido.
 */
export function parseMonthKey(input: string | null | undefined): { year: number; month: number } | null {
  if (!input) return null;
  const trimmed = input.trim();
  const match = trimmed.match(/^([A-Za-zÀ-ÿ]+)\s+(\d{4})$/);
  if (!match) return null;
  const [, monthName, yearStr] = match;
  if (!monthName || !yearStr) return null;
  const month = MONTH_INDEX_PT.get(monthName.toLowerCase());
  if (month === undefined) return null;
  const year = Number(yearStr);
  if (!Number.isInteger(year)) return null;
  return { year, month };
}

/** Inverso de parseMonthKey: 2026, 3 (Abril 0-based) → "Abril 2026". */
export function formatMonthKey(year: number, monthZeroBased: number): string {
  const name = MONTH_NAMES_PT[monthZeroBased];
  if (!name) throw new Error(`Mês inválido: ${monthZeroBased}`);
  return `${name} ${year}`;
}

/** Devolve o primeiro dia do mês (UTC) a partir de Mês_Chave. */
export function monthKeyToDate(monthKey: string): Date | null {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) return null;
  return new Date(Date.UTC(parsed.year, parsed.month, 1));
}

/**
 * Normaliza a coluna A (Data) da sheet Mensalidades. Possíveis formatos:
 *   1. Date nativo (SheetJS com cellDates: true)
 *   2. number (Excel serial — não esperado mas defensivo)
 *   3. string "DD/MM" (ex.: "09/03") — combinado com year inferido do bloco
 *   4. string ISO ou "DD/MM/YYYY"
 *   5. null/empty → herda da linha anterior
 *
 * Retorna a Date resolvida ou null se inválido.
 */
export function normalizeDateCell(
  cell: unknown,
  yearFromBlock: number,
  previousDate: Date | null,
): Date | null {
  if (cell == null || cell === '') {
    return previousDate;
  }
  if (cell instanceof Date) {
    return cell;
  }
  if (typeof cell === 'number') {
    // Excel serial date (1900-based, com bug 1900-02-29). Defensivo apenas.
    const epoch = Date.UTC(1899, 11, 30);
    return new Date(epoch + cell * 86_400_000);
  }
  if (typeof cell === 'string') {
    const s = cell.trim();
    // DD/MM/YYYY
    const fullMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (fullMatch) {
      const [, dd, mm, yyyy] = fullMatch;
      return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
    }
    // DD/MM (assume year do bloco)
    const shortMatch = s.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (shortMatch) {
      const [, dd, mm] = shortMatch;
      return new Date(Date.UTC(yearFromBlock, Number(mm) - 1, Number(dd)));
    }
    // ISO ou outro reconhecível pelo Date constructor
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return previousDate;
}
